import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DocumentScannerRoundedIcon from '@mui/icons-material/DocumentScannerRounded';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import Resume from '../../scenes/redCoins/resume';
import Header from '../Header';

const PaymentMethodSelector = ({
  t,
  quantity,
  totalPrice,
  name,
  email,
  paymentResumeId,
  resume,
  setResume,
  resumePayment,
  setResumePayment,
  setShowCreditCard,
  createSubscription,
  showCreditCard,
  onBack,
  onSubmit,
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const isPix = selectedPaymentMethod === 'pix';
  const isBill = selectedPaymentMethod === 'boleto';
  const isCreditCard = selectedPaymentMethod === 'credit_card';

  return (
    <Box sx={{ maxWidth: 800 }}>
      {/* Header */}
      <Box mb={2}>
        <Header title={t('payment.almostThere')} />
      </Box>

      {/* Botão Voltar */}
      <Button
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#83D84D',
          textTransform: 'none',
          padding: 0,
          marginBottom: 3,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <ArrowBackIcon />
        <Typography fontSize={14} fontWeight={400}>
          {t('common.back')}
        </Typography>
      </Button>

      {/* Card Resumo */}
      <Box
        sx={{
          backgroundColor: '#fff',
          padding: 2,
          borderRadius: 2,
          marginBottom: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Typography sx={{ fontSize: '20px' }} fontWeight="bold" color="#83d84d">
          {t('payment.resume')}
        </Typography>
        <Typography color="text.secondary" gutterBottom sx={{ fontSize: '20px' }}>
          {t('payment.resumeQuantity')}: {quantity} {t('payment.consultation').toLowerCase()}
        </Typography>
        <Typography fontWeight="bold" sx={{ color: '#2C2C2C', fontSize: '20px', fontWeight: 700 }}>
          {t('payment.totalValue')}:{' '}
          {totalPrice.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })}
        </Typography>
      </Box>

      {/* Forma de pagamento */}
      <Typography
        variant="body2"
        gutterBottom
        sx={{ marginBottom: 2, fontSize: '20px', color: '#4F4F4D' }}
      >
        {t('payment.paymentMethod')}:
      </Typography>

      {/* Botão PIX */}
      <Box>
        <Button
          fullWidth
          variant={isPix ? 'contained' : 'outlined'}
          onClick={() => {
            setSelectedPaymentMethod('pix');
          }}
          sx={{
            justifyContent: 'flex-start',
            gap: '8px',
            padding: '16px 20px',
            marginBottom: 2,
            backgroundColor: isPix ? 'rgb(131, 216, 77, 0.2)' : '#fff',
            boxShadow: isPix ? 'none' : '0 1px 3px rgba(0,0,0,0.2)',
            borderColor: isPix ? '#83D84D' : '#ddd',
            border: isPix ? '1px solid' : '0',
            color: isPix ? '#83D84D' : '#000',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: isPix ? '#e8f5e9' : '#f5f5f5',
              borderColor: isPix ? '#83D84D' : '#ddd',
              boxShadow: 'none',
            },
          }}
        >
          <Box
            sx={{
              height: '18px',
              width: '18px',
              border: '1px solid #D9E1E7',
              borderRadius: '50%',
              position: 'relative',
              backgroundColor: '#FFF',
            }}
          >
            {isPix && (
              <Box
                sx={{
                  height: '12px',
                  width: '12px',
                  backgroundColor: '#83D84D',
                  borderRadius: '50%',
                  position: 'absolute',
                  inset: 0,
                  margin: 'auto',
                }}
              />
            )}
          </Box>
          <PixRoundedIcon />
          <Typography variant="body1" fontWeight={isPix ? '500' : '400'}>
            {t('payment.pix')}
          </Typography>
        </Button>
      </Box>

      {/* Botão Cartão de crédito */}
      <Button
        fullWidth
        variant={isCreditCard ? 'contained' : 'outlined'}
        onClick={() => {
          setSelectedPaymentMethod('credit_card');
          setShowCreditCard(true);
        }}
        sx={{
          justifyContent: 'flex-start',
          gap: '8px',
          padding: '16px 20px',
          marginBottom: 2,
          backgroundColor: isCreditCard ? 'rgb(131, 216, 77, 0.2)' : '#fff',
          boxShadow: isCreditCard ? 'none' : '0 1px 3px rgba(0,0,0,0.2)',
          borderColor: isCreditCard ? '#83D84D' : '#ddd',
          border: isCreditCard ? '1px solid' : '0',
          color: isCreditCard ? '#83D84D' : '#000',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: isCreditCard ? '#e8f5e9' : '#f5f5f5',
            borderColor: isCreditCard ? '#83D84D' : '#ddd',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            height: '18px',
            width: '18px',
            border: '1px solid #D9E1E7',
            borderRadius: '50%',
            position: 'relative',
            backgroundColor: '#FFF',
          }}
        >
          {isCreditCard && (
            <Box
              sx={{
                height: '12px',
                width: '12px',
                backgroundColor: '#83D84D',
                borderRadius: '50%',
                position: 'absolute',
                inset: 0,
                margin: 'auto',
              }}
            />
          )}
        </Box>
        <CreditCardIcon />
        <Typography variant="body1" fontWeight={isCreditCard ? '500' : '400'}>
          {t('payment.creditCard')}
        </Typography>
      </Button>

      {/* Botão Boleto bancário */}
      <Button
        fullWidth
        variant={isBill ? 'contained' : 'outlined'}
        onClick={() => {
          setSelectedPaymentMethod('boleto');
        }}
        sx={{
          justifyContent: 'flex-start',
          gap: '8px',
          padding: '16px 20px',
          marginBottom: 2,
          backgroundColor: isBill ? 'rgb(131, 216, 77, 0.2)' : '#fff',
          boxShadow: isBill ? 'none' : '0 1px 3px rgba(0,0,0,0.2)',
          borderColor: isBill ? '#83D84D' : '#ddd',
          border: isBill ? '1px solid' : '0',
          color: isBill ? '#83D84D' : '#000',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: isBill ? '#e8f5e9' : '#f5f5f5',
            borderColor: isBill ? '#83D84D' : '#ddd',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            height: '18px',
            width: '18px',
            border: '1px solid #D9E1E7',
            borderRadius: '50%',
            position: 'relative',
            backgroundColor: '#FFF',
          }}
        >
          {isBill && (
            <Box
              sx={{
                height: '12px',
                width: '12px',
                backgroundColor: '#83D84D',
                borderRadius: '50%',
                position: 'absolute',
                inset: 0,
                margin: 'auto',
              }}
            />
          )}
        </Box>
        <DocumentScannerRoundedIcon />
        <Typography variant="body1" fontWeight={isBill ? '500' : '400'}>
          {t('payment.boleto')}
        </Typography>
      </Button>

      {/* Botão Comprar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={!selectedPaymentMethod}
          onClick={async () => {
            await createSubscription(selectedPaymentMethod);
          }}
          sx={{
            backgroundColor: '#83D84D',
            color: '#2C2C2C',
            textTransform: 'uppercase',
            fontWeight: 'bold',
            padding: '8px 12px',
            '&:hover': {
              backgroundColor: '#45a049',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          {t('payment.buy')}
        </Button>
      </Box>

      {/* Resume Modal */}
      <div>
        {resumePayment && (
          <Resume
            open={resume}
            onClose={() => {
              setResumePayment(false);
              setResume(false);
            }}
            nome={name}
            email={email}
            quantidade={quantity}
            valor={totalPrice}
            resumeId={paymentResumeId}
          />
        )}
      </div>
    </Box>
  );
};

export default PaymentMethodSelector;
